import { Injectable } from '@angular/core';
import {
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import {
  NotificationMessageConfig,
  NotificationMessageComponent,
  warningNotificationMessageConfig,
} from 'src/app/components/shared/notification-message/notification-message.component';

export interface SnackBarData {
  message: string;
  snackBarConfig: SnackBarConfig;
  notificationMessageConfig: NotificationMessageConfig;
}

export interface SnackBarOptions {
  snackBarConfig?: SnackBarConfig;
  notificationMessageConfig?: NotificationMessageConfig;
}

export interface SnackBarConfig {
  iconColour?: string;
  icon?: string;
  verticalPosition?: MatSnackBarVerticalPosition;
  horizontalPosition?: MatSnackBarHorizontalPosition;
  duration?: number;
}

const defaultSnackBarConfig: SnackBarConfig = {
  iconColour: 'adminis-green-500',
  icon: 'close',
  verticalPosition: 'top',
  horizontalPosition: 'center',
  duration: 10000,
};

const warningSnackBarConfig: SnackBarConfig = {
  ...defaultSnackBarConfig,
  iconColour: 'warning-orange-500',
  duration: 0,
};

@Injectable({ providedIn: 'root' })
export class SnackBarService {
  constructor(private snackBar: MatSnackBar) {}

  /**
   * Displays a snackbar with a notification message. Uses the warning snack
   * bar and message config.
   *
   * Config uses default warning options and only overrides values that have
   * been passed in through options.
   *
   * @param message The message to display in the snackbar. This can include
   * inline html for better formatting options.
   * @param options Additional config options that allow overriding of default
   * snack bar and notification message config.
   */
  public showWarningSnackBar(message: string, options?: SnackBarOptions): void {
    options = {
      snackBarConfig: {
        ...warningSnackBarConfig,
        ...options?.snackBarConfig,
      },
      notificationMessageConfig: {
        ...warningNotificationMessageConfig,
        ...options?.notificationMessageConfig,
      },
    };

    this.showSnackBar(message, options);
  }

  /**
   * Displays a snackbar with a notification message. Uses the default snack
   * bar and message config.
   *
   * Config uses default options and only overrides values that have been
   * passed in through options.
   *
   * @param message The message to display in the snackbar. This can include
   * inline html for better formatting options.
   * @param options Additional config options that allow overriding of default
   * snack bar and notification message config.
   */
  public showSnackBar(message: string, options?: SnackBarOptions): void {
    options = {
      snackBarConfig: {
        ...defaultSnackBarConfig,
        ...options?.snackBarConfig,
      },
      notificationMessageConfig: options?.notificationMessageConfig,
    };

    const snackBarData: SnackBarData = {
      message,
      snackBarConfig: options.snackBarConfig,
      notificationMessageConfig: options.notificationMessageConfig,
    };

    const snackBarConfig: MatSnackBarConfig = {
      duration: options.snackBarConfig.duration,
      verticalPosition: options.snackBarConfig.verticalPosition,
      horizontalPosition: options.snackBarConfig.horizontalPosition,
      data: snackBarData,
    };

    this.snackBar.openFromComponent(
      NotificationMessageComponent,
      snackBarConfig,
    );
  }

  /**
   * Closes the snack bar.
   */
  public dismiss(): void {
    this.snackBar.dismiss();
  }
}
